import React, { PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectPMSExportConfiguration, selectPMSType } from 'app/redux/pms/selector';
import PmsExporter, { PMSExportFn } from 'app/pms/exporter/PmsExporter';
import { useIntl } from 'react-intl';
import getFs from 'app/native/node/fs';
import IDisplayableImageEncoderContext from 'app/providers/IDisplayableImageEncoder/context';
import _ from 'lodash';
import AssistoVetExporter, { AssistoVet } from 'app/pms/exporter/AssistoVetExporter';
import GmVetExporter, { GmVet } from 'app/pms/exporter/GmVetExporter';
import VetoPartnerExporter, { VetoPartner } from 'app/pms/exporter/VetoPartnerExporter';
import VetoWinExporter, { VetoWin } from 'app/pms/exporter/VetoWinExporter';
import FileMakerExporter, { FileMaker } from 'app/pms/exporter/FileMakerExporter';
import { PmsTypes } from 'app/pms/parser/types';

export const PMSExportContext = React.createContext<PMSExportFn>(undefined);

const PMSExportProviderImpl = ({ children }: PropsWithChildren<{}>) => {
  const fs = getFs();
  const intl = useIntl();
  const imageEncoder = useContext(IDisplayableImageEncoderContext);
  const [exporters] = useState(
    () =>
      ({
        [AssistoVet.CONFIGURATION_KEY]: new AssistoVetExporter(imageEncoder, fs.promises),
        [GmVet.CONFIGURATION_KEY]: new GmVetExporter(imageEncoder, fs.promises),
        [VetoPartner.CONFIGURATION_KEY]: new VetoPartnerExporter(imageEncoder, fs.promises),
        [VetoWin.CONFIGURATION_KEY]: new VetoWinExporter(imageEncoder, fs.promises),
        [FileMaker.CONFIGURATION_KEY]: new FileMakerExporter(imageEncoder, fs.promises),
      } as const)
  );

  const pmsType: PmsTypes = useSelector(selectPMSType);
  const pmsExportConfiguration = useSelector(selectPMSExportConfiguration);
  const isPMSEnabled = !!(pmsType && pmsExportConfiguration);

  const exportToPMSFn = useMemo(() => {
    if (!isPMSEnabled) return undefined;

    const executeExport: PMSExportFn = async (studyInfo, patientInfo, images) =>
      exporters[pmsType]?.exportDataToPMS(pmsExportConfiguration, studyInfo, patientInfo, images);
    return executeExport;
  }, [pmsExportConfiguration, isPMSEnabled, exporters]);

  useEffect(() => {
    _.forEach(exporters, (exporter) => exporter?.setIntl(intl));
  }, [intl]);
  useEffect(() => {
    _.forEach(exporters, (exporter) => exporter?.setImageEncoder(imageEncoder));
  }, [imageEncoder]);

  return <PMSExportContext.Provider value={exportToPMSFn}>{children}</PMSExportContext.Provider>;
};

function PMSExportProvider({ children }: PropsWithChildren<{}>) {
  const fs = getFs();
  if (!fs) return children;

  return <PMSExportProviderImpl>{children}</PMSExportProviderImpl>;
}

export default PMSExportProvider;
